<template>
  <b-row>
    <b-col cols="12">
      <b-card title="Importador Dinâmico de Arquivos">
        <b-row>
          <!-- Seletor de Layout -->
          <b-col cols="12" md="6">
            <b-form-group label="Layout do Arquivo" label-for="layout-selector">
              <v-select
                id="layout-selector"
                v-model="selectedLayoutId"
                :options="layouts"
                :reduce="layout => layout.id"
                label="nome"
                placeholder="Selecione o layout (Ex: NEOS 9509)"
              />
            </b-form-group>
          </b-col>
          
          <!-- Upload de Arquivo -->
          <b-col cols="12" md="6" v-if="selectedLayoutId">
            <b-form-group label="Selecionar Arquivo" label-for="file-upload">
              <b-form-file
                id="file-upload"
                v-model="file"
                placeholder="Clique ou arraste o arquivo aqui..."
                drop-placeholder="Solte o arquivo..."
                @input="handleUpload"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Ações e Status -->
        <div v-if="batch" class="mt-2 mb-2">
          <div class="d-flex align-items-center mb-1">
            <b-button
              variant="success"
              class="mr-1"
              :disabled="loading || batch.status === 'PROCESSADO' || batch.status === 'PROCESSANDO'"
              @click="handleValidate"
            >
              <feather-icon icon="ShieldIcon" class="mr-50" />
              {{ batch.status === 'PROCESSANDO' ? 'Validando...' : 'Validar Associados' }}
            </b-button>
            
            <b-button
              v-if="batch.valido || batch.status === 'VALIDADO'"
              variant="primary"
              :disabled="loading || batch.status !== 'VALIDADO'"
              @click="handleProcess"
            >
              <feather-icon icon="CheckCircleIcon" class="mr-50" />
              Efetivar Quitação
            </b-button>

            <b-badge v-if="batch.status === 'PROCESSADO'" variant="light-success" class="ml-auto">
              Lote Processado com Sucesso
            </b-badge>
          </div>

          <!-- Barra de Progresso e Contador -->
          <div v-if="batch.status === 'PROCESSANDO' || batch.status === 'PROCESSANDO_QUITACAO'" class="progress-section p-1 border rounded bg-light">
            <div class="d-flex justify-content-between mb-50">
              <span class="font-weight-bolder text-primary">
                {{ batch.status === 'PROCESSANDO' ? 'Validando Lote...' : 'Efetivando Quitação...' }}
              </span>
              <span class="font-small-3">Item {{ batch.itemsProcessed }} de {{ batch.totalItems }}</span>
            </div>
            <b-progress
              :value="batch.itemsProcessed"
              :max="batch.totalItems"
              variant="primary"
              animated
              height="10px"
              class="progress-bar-primary"
            />
          </div>
        </div>

        <!-- Grid de Preview -->
        <div v-if="items.length > 0" class="mt-3">
          <h5 class="mb-1">Prévia dos Dados (Lote #{{ batch.id }})</h5>
          <vue-good-table
            :columns="columns"
            :rows="items"
            styleClass="vgt-table striped"
            :pagination-options="{ enabled: true, perPage: 10 }"
          >
            <template slot="table-row" slot-scope="props">
              <!-- Customização de Status -->
              <span v-if="props.column.field === 'status'">
                <b-badge :variant="statusVariant(props.row.status)">
                  {{ props.row.status }}
                </b-badge>
              </span>
              <span v-else-if="props.column.field === 'dadosMapeados.valor'">
                {{ props.row.dadosMapeados ? formatCurrency(props.row.dadosMapeados[mappedKeys.valor]) : '-' }}
              </span>
              <span v-else-if="props.column.field === 'dadosMapeados.cpf'">
                {{ props.row.dadosMapeados ? props.row.dadosMapeados[mappedKeys.cpf] : '-' }}
              </span>
              <span v-else-if="props.column.field === 'dadosMapeados.competencia'">
                {{ props.row.dadosMapeados ? props.row.dadosMapeados[mappedKeys.competencia] : '-' }}
              </span>
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table>
        </div>

        <b-overlay 
          :show="loading" 
          no-wrap
          opacity="0.8"
          blur="2px"
          variant="light"
        >
          <template #overlay>
            <div class="text-center">
              <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
              <p class="mt-1 font-weight-bolder text-primary">
                {{ loadingMessage || 'Aguarde...' }}
              </p>
            </div>
          </template>
        </b-overlay>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import { 
  BRow, BCol, BCard, BFormGroup, BFormFile, BButton, BBadge, BOverlay,
  BSpinner, BProgress 
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { VueGoodTable } from 'vue-good-table'
import { 
  getLayouts, extractFile, getBatchItems, validateBatch, processBatch,
  getBatchStatus 
} from '@/api/financialModule/dynamicImport'
import Ripple from 'vue-ripple-directive'
import mixinsGlobal from '@/mixins'

export default {
  components: {
    BRow, BCol, BCard, BFormGroup, BFormFile, BButton, BBadge, BOverlay,
    BSpinner, BProgress, vSelect, VueGoodTable
  },
  directives: { Ripple },
  mixins: [mixinsGlobal],
  data() {
    return {
      layouts: [],
      selectedLayoutId: null,
      file: null,
      batch: null,
      items: [],
      loading: false,
      pollingInterval: null,
      mappedKeys: {
        cpf: 'cpf',
        valor: 'valor',
        competencia: 'competencia'
      },
      columns: [
        { label: 'Linha', field: 'numeroLinha', type: 'number', width: '80px' },
        { label: 'Identificador (CPF)', field: 'dadosMapeados.cpf' },
        { label: 'Valor', field: 'dadosMapeados.valor' },
        { label: 'Competência', field: 'dadosMapeados.competencia' },
        { label: 'Status Sistema', field: 'status' },
        { label: 'Mensagem', field: 'observacao' },
      ]
    }
  },
  methods: {
    async loadInitialData() {
      try {
        const response = await getLayouts()
        this.layouts = response.data
      } catch (error) {
        this.MensagemError('Erro ao carregar layouts de importação')
      }
    },
    async handleUpload() {
      if (!this.file || !this.selectedLayoutId) return
      
      this.loadingMessage = 'Lendo e extraindo dados do arquivo...'
      this.loading = true
      try {
        // Find keys in layout mappings
        const layout = this.layouts.find(l => l.id === this.selectedLayoutId)
        if (layout && layout.mappings) {
          layout.mappings.forEach(m => {
            if (m.usoCampo === 'IDENTIFICADOR_SOCIO_CPF') this.mappedKeys.cpf = m.campoDestino
            if (m.usoCampo === 'VALOR_PAGAMENTO') this.mappedKeys.valor = m.campoDestino
            if (m.usoCampo === 'DATA_COMPETENCIA') this.mappedKeys.competencia = m.campoDestino
          })
        }

        const response = await extractFile(this.file, this.selectedLayoutId)
        this.batch = response.data
        await this.loadItems()
        this.showMessageSuccess('Extração', 'Arquivo lido com sucesso. Verifique a prévia abaixo.')
      } catch (error) {
        this.MensagemError('Erro ao processar arquivo. Verifique o layout selecionado.')
      } finally {
        this.loading = false
      }
    },
    async loadItems() {
      if (!this.batch) return
      try {
        const response = await getBatchItems(this.batch.id)
        // Otimização: Object.freeze impede o Vue de tornar cada campo reativo,
        // o que causa travamento em listas grandes.
        this.items = Object.freeze(response.data)
      } catch (e) {
        console.error('Erro ao carregar itens:', e)
      }
    },
    async handleValidate() {
      console.log('Iniciando validação do lote (Async):', this.batch ? this.batch.id : 'NULO')
      this.loadingMessage = 'Iniciando validação...'
      this.loading = true
      try {
        if (!this.batch || !this.batch.id) {
          throw new Error('ID do lote não encontrado.')
        }
        const response = await validateBatch(this.batch.id)
        this.batch = response.data
        this.loading = false // Libera o overlay para mostrar a barra de progresso
        this.startPolling()
      } catch (error) {
        console.error('Erro ao disparar validação:', error)
        this.MensagemError('Erro ao iniciar validação: ' + (error.message || 'Erro desconhecido'))
        this.loading = false
      }
    },
    startPolling() {
      if (this.pollingInterval) clearInterval(this.pollingInterval)
      
      this.pollingInterval = setInterval(async () => {
        // Segurança: Evita erro se o lote não estiver inicializado
        if (!this.batch || !this.batch.id) return
        
        try {
          const response = await getBatchStatus(this.batch.id)
          
          if (response && response.data) {
            this.batch = response.data
            
            if (this.batch.status === 'VALIDADO' || this.batch.status === 'PROCESSADO') {
              clearInterval(this.pollingInterval)
              
              const isFinal = this.batch.status === 'PROCESSADO'
              this.loadingMessage = isFinal ? 'Finalizando...' : 'Carregando prévia dos dados...'
              this.loading = true
              
              await this.loadItems()
              this.loading = false
              
              if (isFinal) {
                this.showMessageSuccess('Sucesso', 'Quitação realizada com sucesso para os registros válidos.')
              } else {
                this.showMessageSuccess('Validação', 'Conciliação finalizada.')
              }
            } else if (this.batch.status === 'ERRO') {
              clearInterval(this.pollingInterval)
              this.loading = false
              this.MensagemError('Ocorreu um erro durante o processamento do lote.')
            }
          }
        } catch (e) {
          console.error('Erro no polling:', e)
        }
      }, 1000)
    },
    async handleProcess() {
      this.loadingMessage = 'Efetivando quitação dos registros válidos no sistema...'
      this.loading = true
      try {
        const response = await processBatch(this.batch.id)
        this.batch = response.data
        this.loading = false
        this.startPolling() // Inicia polling para acompanhar efetivação
      } catch (error) {
        console.error('Erro na quitação:', error)
        this.MensagemError('Erro ao efetivar quitação.')
        this.loading = false
      }
    },
    statusVariant(status) {
      if (status === 'OK') return 'light-success'
      if (status === 'DIVERGENTE') return 'light-warning'
      if (status === 'ERRO' || status === 'NAO_ENCONTRADO') return 'light-danger'
      return 'light-secondary'
    },
    formatCurrency(value) {
      if (!value) return '-'
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
    }
  },
  mounted() {
    this.loadInitialData()
  },
  beforeDestroy() {
    if (this.pollingInterval) clearInterval(this.pollingInterval)
  }
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-good-table.scss";
</style>
